import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Mail, Loader2, Plus, X, Truck } from "lucide-react";
import { buildRouteSheetHtml } from "@/lib/routeSheet";
import api from "@/lib/api";
import { toast } from "sonner";

const inputCls = "focus-visible:ring-2 focus-visible:ring-[#F97316]/30";

function AddPersonForm({ onAdded }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("driver");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!name.trim() || !email.trim()) {
      toast.error("Name and email are required");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/drivers", { name, email, role });
      onAdded(data);
      setName("");
      setEmail("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex flex-wrap items-end gap-2 rounded-md border border-dashed border-border p-2.5 bg-white">
      <div className="flex-1 min-w-[120px]">
        <Label className="text-[10px] font-bold uppercase text-[#57534E]">Name</Label>
        <Input value={name} onChange={(e) => setName(e.target.value)} className={`mt-1 h-8 ${inputCls}`} data-testid="driver-new-name" />
      </div>
      <div className="flex-1 min-w-[160px]">
        <Label className="text-[10px] font-bold uppercase text-[#57534E]">Email</Label>
        <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className={`mt-1 h-8 ${inputCls}`} data-testid="driver-new-email" />
      </div>
      <div>
        <Label className="text-[10px] font-bold uppercase text-[#57534E]">Role</Label>
        <Select value={role} onValueChange={setRole}>
          <SelectTrigger className="mt-1 h-8 w-[120px]" data-testid="driver-new-role">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="driver">Driver</SelectItem>
            <SelectItem value="dispatcher">Dispatcher</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <Button size="sm" onClick={save} disabled={saving} className="bg-[#1C1917] hover:bg-black text-white h-8" data-testid="driver-save-new-button">
        {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Save"}
      </Button>
    </div>
  );
}

export default function DriverSendModal({ open, onOpenChange, routeSheetData }) {
  const [people, setPeople] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [extraEmails, setExtraEmails] = useState("");
  const [ccOwner, setCcOwner] = useState(false);
  const [attachPdf, setAttachPdf] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (open) api.get("/drivers").then(({ data }) => setPeople(data)).catch(() => {});
  }, [open]);

  const toggle = (id) =>
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const removePerson = async (id) => {
    await api.delete(`/drivers/${id}`).catch(() => {});
    setPeople((prev) => prev.filter((p) => p.id !== id));
    setSelectedIds((prev) => prev.filter((x) => x !== id));
  };

  const typed = extraEmails.split(",").map((e) => e.trim()).filter((e) => e.includes("@"));
  const recipients = [
    ...people.filter((p) => selectedIds.includes(p.id)).map((p) => p.email),
    ...typed,
  ];

  const html = buildRouteSheetHtml(routeSheetData);

  const send = async () => {
    if (!recipients.length) {
      toast.error("Pick a saved driver/dispatcher or type an email");
      return;
    }
    setSending(true);
    try {
      const firstDrop = routeSheetData.drops?.[0]?.address || "route";
      const { data } = await api.post("/send-route", {
        recipient_emails: recipients,
        subject: `K&G Route Sheet — ${routeSheetData.schedule?.pickupDate || "TBD pickup"} — ${firstDrop}`,
        html_content: html,
        cc_owner: ccOwner,
        attach_pdf: attachPdf,
        base_url: window.location.origin,
        pdf_filename: "KG-Route-Sheet.pdf",
      });
      toast.success(data.message);
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send route sheet");
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col p-0" data-testid="driver-modal">
        <DialogHeader className="p-5 border-b border-border">
          <DialogTitle className="text-[#1C1917] flex items-center gap-2">
            <Truck className="h-5 w-5 text-[#F97316]" /> Send Route to Driver / Dispatcher
          </DialogTitle>
          <DialogDescription>
            Emails the route sheet — stops, customer phones, schedule, equipment and driver bonus. No customer pricing is included.
          </DialogDescription>
        </DialogHeader>

        <div className="px-5 py-3 bg-stone-50 border-b border-border space-y-3">
          <div>
            <div className="flex items-center justify-between">
              <Label className="text-[11px] font-bold uppercase text-[#57534E]">Saved drivers &amp; dispatchers</Label>
              <Button variant="ghost" size="sm" onClick={() => setAddOpen((v) => !v)} className="h-7 text-[#EA580C]" data-testid="driver-add-button">
                <Plus className="h-3.5 w-3.5 mr-1" /> Add new
              </Button>
            </div>
            {people.length === 0 && !addOpen && (
              <p className="text-xs text-[#57534E] mt-1">No one saved yet — click "Add new" or type an email below.</p>
            )}
            <div className="mt-1 flex flex-wrap gap-2" data-testid="driver-select-list">
              {people.map((p, i) => (
                <label
                  key={p.id}
                  className={`flex items-center gap-2 rounded-md border px-2.5 py-1.5 cursor-pointer bg-white ${
                    selectedIds.includes(p.id) ? "border-[#F97316] bg-[#FFF7ED]" : "border-border"
                  }`}
                  data-testid={`driver-row-${i}`}
                >
                  <Checkbox
                    checked={selectedIds.includes(p.id)}
                    onCheckedChange={() => toggle(p.id)}
                    className="data-[state=checked]:bg-[#F97316] data-[state=checked]:border-[#F97316]"
                    data-testid={`driver-checkbox-${i}`}
                  />
                  <span className="text-sm text-[#1C1917] font-medium">{p.name}</span>
                  <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${p.role === "dispatcher" ? "bg-stone-200 text-stone-700" : "bg-[#FFEDD5] text-[#EA580C]"}`}>
                    {p.role}
                  </span>
                  <span className="text-xs text-[#57534E]">{p.email}</span>
                  <button type="button" onClick={(e) => { e.preventDefault(); removePerson(p.id); }} className="text-stone-400 hover:text-red-600" data-testid={`driver-delete-${i}`} aria-label="Remove">
                    <X className="h-3.5 w-3.5" />
                  </button>
                </label>
              ))}
            </div>
            {addOpen && <div className="mt-2"><AddPersonForm onAdded={(p) => { setPeople((prev) => [...prev, p]); setSelectedIds((prev) => [...prev, p.id]); setAddOpen(false); }} /></div>}
          </div>

          <div className="flex flex-wrap items-end gap-3">
            <div className="flex-1 min-w-[220px]">
              <Label className="text-[11px] font-bold uppercase text-[#57534E]">Other emails (comma-separated)</Label>
              <Input
                type="text"
                placeholder="driver@example.com"
                value={extraEmails}
                onChange={(e) => setExtraEmails(e.target.value)}
                className={`mt-1 ${inputCls}`}
                data-testid="driver-extra-emails"
              />
            </div>
            <Button onClick={send} disabled={sending} className="bg-[#F97316] hover:bg-[#EA580C] text-white shrink-0" data-testid="driver-send-email-button">
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
              <span className="ml-1.5">Send{recipients.length ? ` (${recipients.length})` : ""}</span>
            </Button>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <label className="flex items-center gap-2 cursor-pointer" data-testid="driver-attach-pdf">
              <Checkbox checked={attachPdf} onCheckedChange={(v) => setAttachPdf(!!v)} />
              <span className="text-xs text-[#1C1917]">Include PDF attachment</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer" data-testid="driver-cc-owner">
              <Checkbox checked={ccOwner} onCheckedChange={(v) => setCcOwner(!!v)} />
              <span className="text-xs text-[#1C1917]">Send a copy to the owner (ike@kgmowers.com)</span>
            </label>
          </div>
        </div>

        <div className="overflow-auto p-4 bg-stone-200" data-testid="driver-preview">
          <iframe
            title="Route Sheet Preview"
            srcDoc={html}
            className="w-full bg-white rounded-md border border-border"
            style={{ height: "48vh" }}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}
